import React, { useMemo, useState } from 'react';
import '../styles/PredictorPage.css';
import '../styles/ToolsPage.css';
import { ELEMENT_INFO, PERIODIC_TABLE } from './tools/elementCatalog';

const ELEMENT_DENSITIES = {
  Al: 2.7,
  Ti: 4.506,
  V: 6.11,
  Cr: 7.19,
  Mn: 7.21,
  Fe: 7.874,
  Co: 8.9,
  Ni: 8.908,
  Cu: 8.96,
  Zn: 7.14,
  Zr: 6.52,
  Nb: 8.57,
  Mo: 10.28,
  Sn: 7.31,
  Ag: 10.49,
  Hf: 13.31,
  Ta: 16.69,
  W: 19.25,
  Pb: 11.34,
  Au: 19.32,
};

const AVAILABLE_ELEMENTS = Object.keys(ELEMENT_INFO);

const parseNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

function MixtureDensityPage() {
  const [basis, setBasis] = useState('atomic');
  const [selectedElements, setSelectedElements] = useState({});
  const [composition, setComposition] = useState({});
  const [densities, setDensities] = useState({});
  const [atomicWeights, setAtomicWeights] = useState({});

  const selectedList = useMemo(
    () => Object.keys(selectedElements).filter((element) => selectedElements[element]),
    [selectedElements],
  );

  const handleElementToggle = (element) => {
    if (!AVAILABLE_ELEMENTS.includes(element)) return;
    const isSelected = !!selectedElements[element];

    setSelectedElements((prev) => {
      const next = { ...prev };
      if (isSelected) {
        delete next[element];
      } else {
        next[element] = true;
      }
      return next;
    });

    if (isSelected) {
      setComposition((prev) => {
        const next = { ...prev };
        delete next[element];
        return next;
      });
      setDensities((prev) => {
        const next = { ...prev };
        delete next[element];
        return next;
      });
      setAtomicWeights((prev) => {
        const next = { ...prev };
        delete next[element];
        return next;
      });
      return;
    }

    const defaultDensity = ELEMENT_DENSITIES[element];
    const defaultAtomicWeight = ELEMENT_INFO[element]?.mass;
    setComposition((prev) => ({ ...prev, [element]: prev[element] ?? '' }));
    setDensities((prev) => ({
      ...prev,
      [element]: prev[element] ?? (defaultDensity ? String(defaultDensity) : ''),
    }));
    setAtomicWeights((prev) => ({
      ...prev,
      [element]: prev[element] ?? (defaultAtomicWeight ? String(defaultAtomicWeight) : ''),
    }));
  };

  const handleCompositionChange = (element, value) => {
    setComposition((prev) => ({ ...prev, [element]: value }));
  };

  const handleDensityChange = (element, value) => {
    setDensities((prev) => ({ ...prev, [element]: value }));
  };

  const handleAtomicWeightChange = (element, value) => {
    setAtomicWeights((prev) => ({ ...prev, [element]: value }));
  };

  const handleClear = () => {
    setSelectedElements({});
    setComposition({});
    setDensities({});
    setAtomicWeights({});
  };

  const totalInput = useMemo(
    () =>
      selectedList.reduce((sum, element) => {
        const value = parseNumber(composition[element]);
        return sum + (value && value > 0 ? value : 0);
      }, 0),
    [composition, selectedList],
  );

  const result = useMemo(() => {
    const entries = selectedList
      .map((element) => ({
        element,
        fraction: parseNumber(composition[element]),
        density: parseNumber(densities[element]),
        atomicWeight: parseNumber(atomicWeights[element]),
      }))
      .filter((row) => row.fraction !== null && row.fraction > 0);

    if (!entries.length) {
      return { density: null, sum: 0, issues: [] };
    }

    const issues = [];
    let sum = 0;
    entries.forEach((row) => {
      sum += row.fraction;
      if (!row.density || row.density <= 0) {
        issues.push(`${row.element}: density is required.`);
      }
      if (basis === 'atomic' && (!row.atomicWeight || row.atomicWeight <= 0)) {
        issues.push(`${row.element}: atomic weight is required.`);
      }
    });

    if (issues.length) {
      return { density: null, sum, issues };
    }

    if (sum <= 0) {
      return { density: null, sum, issues: ['Total fraction must be greater than 0.'] };
    }

    const normalized = entries.map((row) => ({ ...row, fraction: row.fraction / sum }));

    let weightFractions = normalized;
    if (basis === 'atomic') {
      const weightRaw = normalized.map((row) => row.fraction * row.atomicWeight);
      const weightTotal = weightRaw.reduce((acc, val) => acc + val, 0);
      if (weightTotal <= 0) {
        return { density: null, sum, issues: ['Atomic weights must be greater than 0.'] };
      }
      weightFractions = normalized.map((row, idx) => ({
        ...row,
        weightFraction: weightRaw[idx] / weightTotal,
      }));
    } else {
      weightFractions = normalized.map((row) => ({ ...row, weightFraction: row.fraction }));
    }

    const denominator = weightFractions.reduce((acc, row) => acc + row.weightFraction / row.density, 0);
    if (!denominator) {
      return { density: null, sum, issues: ['Density values must be greater than 0.'] };
    }
    return { density: 1 / denominator, sum, issues: [] };
  }, [atomicWeights, basis, composition, densities, selectedList]);

  const normalizedNote =
    totalInput > 0 && Math.abs(totalInput - 100) > 0.1
      ? `Normalized from ${totalInput.toFixed(2)}%.`
      : totalInput > 0
        ? 'Sum = 100%.'
        : 'Enter element fractions to calculate.';

  return (
    <div className="predictor-page">
      <div className="predictor-container">
        <div className="predictor-header">
          <h2>Rule of Mixtures Density</h2>
          <p className="predictor-subtitle">
            Estimate theoretical density using the rule of mixtures. Select elements on the periodic table to start.
          </p>
        </div>

        <div className="predictor-main">
          <div className="element-panel">
            <div className="panel-header">
              <h3>Select Elements</h3>
              <p className="panel-description">Click elements to build your alloy composition.</p>
            </div>
            <div className="periodic-table-container">
              <div className="periodic-table">
                {PERIODIC_TABLE.map((row, rowIndex) => (
                  <div key={rowIndex} className="periodic-row">
                    {row.map((element, colIndex) => {
                      const isAvailable = element && AVAILABLE_ELEMENTS.includes(element);
                      const isSelected = element && selectedElements[element];
                      return (
                        <div
                          key={`${rowIndex}-${colIndex}`}
                          className={`periodic-cell ${element ? (isAvailable ? 'available' : 'unavailable') : 'empty'} ${
                            isSelected ? 'selected' : ''
                          }`}
                          onClick={() => (isAvailable ? handleElementToggle(element) : null)}
                          title={element ? `${element} - ${ELEMENT_INFO[element]?.name || 'Unknown'}` : ''}
                        >
                          {element && (
                            <>
                              <div className="element-number">{ELEMENT_INFO[element]?.number ?? ''}</div>
                              <div className="element-symbol">{element}</div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="composition-panel">
            <div className="panel-header">
              <h3>Composition Input</h3>
              <p className="panel-description">Enter atomic% or weight% for each selected element.</p>
            </div>

            <div className="tools-section tools-mixture-controls">
              <div>
                <label className="tools-label">Input basis</label>
                <select className="tools-select" value={basis} onChange={(e) => setBasis(e.target.value)}>
                  <option value="atomic">Atomic % (at%)</option>
                  <option value="weight">Weight % (wt%)</option>
                </select>
              </div>
              <button className="tools-secondary-button" type="button" onClick={handleClear} disabled={!selectedList.length}>
                Clear selection
              </button>
            </div>

            <div className="composition-input-section">
              {selectedList.length ? (
                <>
                  <div className="composition-grid">
                    {selectedList.map((element) => (
                      <div key={element} className="composition-item">
                        <label className="composition-label">
                          <span className="element-symbol-large">{element}</span>
                          <span className="element-name-small">{ELEMENT_INFO[element]?.name}</span>
                        </label>
                        <div className="input-wrapper">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={composition[element] || ''}
                            onChange={(e) => handleCompositionChange(element, e.target.value)}
                            placeholder="0.00"
                            className="composition-input"
                          />
                          <span className="input-unit">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="composition-summary">
                    <div className={`total-composition ${totalInput > 0 ? 'valid' : 'invalid'}`}>
                      <span className="total-label">Total:</span>
                      <span className="total-value">{totalInput.toFixed(2)}%</span>
                      {totalInput > 0 && Math.abs(totalInput - 100) >= 0.01 && (
                        <span className="total-error">Will normalize to 100%</span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="no-elements-selected">
                  <div className="no-elements-icon">x</div>
                  <p>No elements selected</p>
                  <p className="no-elements-hint">Click on elements in the periodic table to start</p>
                </div>
              )}
              <p className="tools-hint">{normalizedNote}</p>
            </div>
          </div>
        </div>

        {selectedList.length ? (
          <div className="tools-card">
            <p className="tools-label">Element properties</p>
            <div className="tools-mixture-table">
              <table>
                <thead>
                  <tr>
                    <th>Element</th>
                    <th>Density (g/cm^3)</th>
                    {basis === 'atomic' ? <th>Atomic weight</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {selectedList.map((element) => (
                    <tr key={element}>
                      <td>{element}</td>
                      <td>
                        <input
                          className="tools-input tools-mixture-input"
                          type="number"
                          min="0"
                          step="0.001"
                          value={densities[element] || ''}
                          onChange={(e) => handleDensityChange(element, e.target.value)}
                          placeholder={ELEMENT_DENSITIES[element] ? String(ELEMENT_DENSITIES[element]) : '0'}
                        />
                      </td>
                      {basis === 'atomic' ? (
                        <td>
                          <input
                            className="tools-input tools-mixture-input"
                            type="number"
                            min="0"
                            step="0.001"
                            value={atomicWeights[element] || ''}
                            onChange={(e) => handleAtomicWeightChange(element, e.target.value)}
                            placeholder={ELEMENT_INFO[element]?.mass ? String(ELEMENT_INFO[element].mass) : '0'}
                          />
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="tools-hint">Auto-fill uses common densities and atomic weights. You can override any value.</p>
            {result.issues.length ? <p className="tools-error">{result.issues[0]}</p> : null}
          </div>
        ) : null}

        <div className="tools-stats-grid">
          <div className="tools-stat">
            <p className="tools-stat-label">Theoretical density</p>
            <p className="tools-stat-value">{result.density ? result.density.toFixed(3) : '-'}</p>
          </div>
          <div className="tools-stat">
            <p className="tools-stat-label">Input sum</p>
            <p className="tools-stat-value">{totalInput ? `${totalInput.toFixed(2)}%` : '-'}</p>
          </div>
          <div className="tools-stat">
            <p className="tools-stat-label">Basis</p>
            <p className="tools-stat-value">{basis === 'atomic' ? 'Atomic %' : 'Weight %'}</p>
          </div>
        </div>

        <div className="tools-card">
          <p className="tools-label">Notes</p>
          <ul className="tools-mixture-notes">
            <li>Rule of mixtures (weight%): rho = 1 / sum(w_i / rho_i).</li>
            <li>Atomic% is converted to weight% using atomic weights, then applied to the formula above.</li>
            <li>Percentages are normalized to 100% if the total is not 100.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default MixtureDensityPage;
